import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FiSearch, FiChevronDown, FiPhone, FiMapPin, FiDollarSign, FiX, FiLoader, FiBookOpen } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { colors, spacing, borderRadius, typography } from '../theme/colors';
import { formatAmount } from '../utils/documentNumbering';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { getLinkedAccount } from '../utils/coaInitializer';
import { focusNext } from '../utils/focusNext';
import { getCustomersCached } from '../utils/masterDataStore';

const VendorSelect = ({ vendors, selectedVendorId, onChange, disabled, placeholder = '-- Select Vendor --', allOption = null, balanceOnDemand = false }) => {
  const { activeCompany, activeBranch, activeFinancialYear, isModuleEnabled } = useAuth();
  const navigate = useNavigate();
  const unifiedMode = isModuleEnabled && isModuleEnabled('unified_party_picker');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [vendorBalance, setVendorBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [extraCustomers, setExtraCustomers] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Unified mode: also load customers and merge them in
  useEffect(() => {
    if (!unifiedMode || !activeCompany?.id) { setExtraCustomers([]); return; }
    (async () => {
      try {
        // Tier 1/6 (Firebase cost): device-cached customers instead of a full
        // read on every unified-picker mount.
        const all = await getCustomersCached(activeCompany.id);
        const customers = all
          .filter(c => c.isActive !== false)
          .map(c => ({
            ...c,
            // Map to vendor-shape fields for display compatibility
            vendorName: c.companyName || c.vendorName || '',
            vendorCode: c.customerCode || c.vendorCode || '',
            _partyType: 'customer'
          }));
        setExtraCustomers(customers);
      } catch (err) { console.error('Error loading customers for unified picker:', err); }
    })();
  }, [unifiedMode, activeCompany?.id]);

  // Tag native vendors with _partyType so list rendering can show the badge
  const allParties = useMemo(() => {
    const tagged = (vendors || []).map(v => ({ ...v, _partyType: v._partyType || 'vendor' }));
    if (!unifiedMode) return tagged;
    return [...tagged, ...extraCustomers];
  }, [vendors, extraCustomers, unifiedMode]);

  const selectedVendor = useMemo(() =>
    allParties.find(v => v.id === selectedVendorId) || null,
    [allParties, selectedVendorId]
  );

  // Calculate vendor balance from subTransactions
  const fetchVendorBalance = useCallback(async (vendor) => {
    if (!activeCompany?.id) {
      setVendorBalance(vendor?.openingBalance || 0);
      return;
    }

    setLoadingBalance(true);
    try {
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      let totalDebits = 0;
      let totalCredits = 0;
      let foundEntries = false;
      let linkedAccountId = vendor?.linkedAccountId;

      // Helper function to process entries - include both Posted AND Draft (for opening balances)
      const branchFilter = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;
      // ACTIVE FINANCIAL YEAR ONLY — see CustomerSelect. The new year's opening
      // balance already contains last year's closing, so counting both doubled it.
      const fyFilter = activeFinancialYear?.id || null;
      const processEntries = (snapshot) => {
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          // Filter by branch if active
          if (branchFilter && data.branchId !== branchFilter) return;
          if (fyFilter && data.financialYearId && data.financialYearId !== fyFilter) return;
          // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
          if (data.status === 'Posted' || (data.status === 'Draft' && data.tranTypeCode === 'OB')) {
            totalDebits += data.debitAmount || 0;
            totalCredits += data.creditAmount || 0;
          }
        });
        return !snapshot.empty;
      };

      // Strategy 0: If vendor doesn't have linkedAccountId, look up the linked account by vendorId
      if (!linkedAccountId && vendor?.id) {
        try {
          const linkedAccount = await getLinkedAccount(activeCompany.id, 'vendor', vendor.id);
          if (linkedAccount?.id) {
            linkedAccountId = linkedAccount.id;
          }
        } catch (err) {
          console.log('Could not find linked account for vendor:', err);
        }
      }

      // Strategy 1: Query by linkedAccountId (document ID)
      if (linkedAccountId) {
        const q1 = query(subTransRef, where('accountId', '==', linkedAccountId));
        const snapshot1 = await getDocs(q1);
        if (processEntries(snapshot1)) {
          foundEntries = true;
        }
      }

      // Strategy 2: Query by accountCode if linkedAccountCode exists
      if (!foundEntries && vendor?.linkedAccountCode) {
        const q2 = query(subTransRef, where('accountCode', '==', vendor.linkedAccountCode));
        const snapshot2 = await getDocs(q2);
        if (processEntries(snapshot2)) {
          foundEntries = true;
        }
      }

      // Strategy 3: Query by accountName containing vendor name
      if (!foundEntries && vendor?.vendorName) {
        const q3 = query(subTransRef, where('accountName', '==', `Vendor: ${vendor.vendorName}`));
        const snapshot3 = await getDocs(q3);
        if (processEntries(snapshot3)) {
          foundEntries = true;
        }
      }

      // Strategy 4: Query by partyId (vendor document ID)
      if (!foundEntries && vendor?.id) {
        const q4 = query(subTransRef, where('partyId', '==', vendor.id));
        const snapshot4 = await getDocs(q4);
        if (processEntries(snapshot4)) {
          foundEntries = true;
        }
      }

      // If still no entries found, fallback to openingBalance from vendor master
      if (!foundEntries) {
        setVendorBalance(vendor?.openingBalance || 0);
        return;
      }

      // For vendor (payable account): Credits increase balance (what we owe), Debits decrease (payments)
      // Balance = Credits - Debits (positive means we owe them)
      const balance = totalCredits - totalDebits;
      setVendorBalance(balance);
    } catch (error) {
      console.error('Error fetching vendor balance:', error);
      // Fallback to opening balance on error
      setVendorBalance(vendor?.openingBalance || 0);
    } finally {
      setLoadingBalance(false);
    }
  }, [activeCompany, activeBranch]);

  // Fetch balance when selected vendor changes (skip in filter mode).
  // Firebase cost: when `balanceOnDemand`, don't auto-scan the ledger on every
  // vendor pick — fetched only when the user clicks "View Balance".
  useEffect(() => {
    if (allOption) return;
    setVendorBalance(null);
    if (!balanceOnDemand && selectedVendor) {
      fetchVendorBalance(selectedVendor);
    }
  }, [selectedVendor, fetchVendorBalance, allOption, balanceOnDemand]);

  const filteredVendors = useMemo(() => {
    if (!search.trim()) return allParties;
    const q = search.toLowerCase();
    return allParties.filter(v =>
      v.vendorName?.toLowerCase().includes(q) ||
      v.vendorCode?.toLowerCase().includes(q) ||
      v.phone?.includes(q) ||
      v.city?.toLowerCase().includes(q)
    );
  }, [allParties, search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (vendor, { advanceFocus = false } = {}) => {
    // Pass full party object as 2nd arg so forms can read _partyType + linkedAccountId
    // (backward-compatible: forms ignoring the 2nd arg still work)
    onChange(vendor.id, vendor);
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(0);
    if (advanceFocus) {
      setTimeout(() => { if (triggerRef.current) focusNext(triggerRef.current); }, 30);
    } else {
      setTimeout(() => triggerRef.current?.focus(), 30);
    }
  };

  const isAllSelected = allOption ? selectedVendorId === allOption.value : !selectedVendorId;

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(allOption ? allOption.value : '', null);
    setSearch('');
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  useEffect(() => { setHighlightedIndex(0); }, [search]);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen && selectedVendor && e.key === 'Enter') {
      e.preventDefault();
      focusNext(triggerRef.current);
      return;
    }
    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      handleOpen();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filteredVendors.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const v = filteredVendors[highlightedIndex];
      if (v) handleSelect(v, { advanceFocus: true });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearch('');
      setTimeout(() => triggerRef.current?.focus(), 30);
    } else if (e.key === 'Tab') {
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <SelectTrigger
        ref={triggerRef}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={disabled ? -1 : 0}
        $disabled={disabled}
        $hasValue={!!selectedVendor}
      >
        {isOpen ? (
          <SearchInputWrapper>
            <FiSearch size={14} />
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search vendor by name, code, phone, city..."
              onClick={e => e.stopPropagation()}
            />
          </SearchInputWrapper>
        ) : (
          <TriggerText $hasValue={!!selectedVendor}>
            {selectedVendor ? `${selectedVendor.vendorCode} - ${selectedVendor.vendorName}` : (allOption ? allOption.label : placeholder)}
          </TriggerText>
        )}
        <TriggerIcons>
          {selectedVendor && !isAllSelected && !disabled && <ClearBtn onClick={handleClear}><FiX size={14} /></ClearBtn>}
          <FiChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        </TriggerIcons>
      </SelectTrigger>

      {isOpen && (
        <Dropdown>
          {filteredVendors.length === 0 ? (
            <NoResults>No vendors found</NoResults>
          ) : (
            filteredVendors.map((v, idx) => {
              const isCustomer = v._partyType === 'customer';
              return (
                <DropdownItem
                  key={`${v._partyType}_${v.id}`}
                  onClick={() => handleSelect(v, { advanceFocus: true })}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  $selected={v.id === selectedVendorId}
                  $highlighted={idx === highlightedIndex}
                >
                  {unifiedMode && (
                    <PartyBadge $customer={isCustomer}>{isCustomer ? 'C' : 'V'}</PartyBadge>
                  )}
                  <VendorCode>{v.vendorCode}</VendorCode>
                  <VendorName>{v.vendorName}</VendorName>
                  {v.city && <VendorCity>{v.city}</VendorCity>}
                </DropdownItem>
              );
            })
          )}
        </Dropdown>
      )}

      {selectedVendor && !allOption && (
        <VendorInfoPanel>
          <InfoRow>
            <InfoChip><FiMapPin size={12} /> {selectedVendor.address ? `${selectedVendor.address}${selectedVendor.city ? ', ' + selectedVendor.city : ''}` : 'No address'}</InfoChip>
            <InfoChip><FiPhone size={12} /> {selectedVendor.phone || selectedVendor.mobile || 'No phone'}</InfoChip>
            <InfoChip $highlight $negative={vendorBalance > 0}>
              <FiDollarSign size={12} />
              {loadingBalance ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FiLoader size={10} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
                </span>
              ) : (balanceOnDemand && vendorBalance === null) ? (
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={(e) => { e.stopPropagation(); if (selectedVendor) fetchVendorBalance(selectedVendor); }}
                  title="Click to calculate this vendor's balance (reads the ledger)"
                >View Balance</span>
              ) : (
                <>Balance: {formatAmount(vendorBalance || 0)}</>
              )}
            </InfoChip>
            {selectedVendor.linkedAccountId && (
              <LedgerLinkChip
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/reports/ledger', { state: { filterAccountId: selectedVendor.linkedAccountId } });
                }}
                title="Open this vendor's ledger"
              >
                <FiBookOpen size={12} /> View Ledger
              </LedgerLinkChip>
            )}
          </InfoRow>
        </VendorInfoPanel>
      )}
    </Wrapper>
  );
};

// ===== STYLED =====
const Wrapper = styled.div`position:relative;`;
const SelectTrigger = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:${spacing.sm} ${spacing.md};
  border:1px solid ${props => props.$hasValue ? colors.primary.main + '60' : colors.border.light};
  border-radius:${borderRadius.sm};font-size:${typography.fontSize.sm};
  color:${colors.text.primary};background:${props => props.$disabled ? colors.neutral[100] : colors.background.default};
  cursor:${props => props.$disabled ? 'not-allowed' : 'pointer'};
  min-height:36px;transition:border-color 0.2s, box-shadow 0.2s;
  &:hover{border-color:${props => props.$disabled ? colors.border.light : colors.primary.main};}
  &:focus{outline:none;border-color:${colors.primary.main};box-shadow:0 0 0 2px ${colors.primary.main}30;}
`;
const TriggerText = styled.span`color:${props => props.$hasValue ? colors.text.primary : colors.text.tertiary};`;
const TriggerIcons = styled.div`display:flex;align-items:center;gap:4px;color:${colors.text.secondary};`;
const ClearBtn = styled.button`
  display:flex;align-items:center;justify-content:center;
  width:20px;height:20px;background:${colors.neutral[200]};border:none;
  border-radius:50%;cursor:pointer;color:${colors.text.secondary};
  &:hover{background:${colors.error.main};color:white;}
`;
const SearchInputWrapper = styled.div`display:flex;align-items:center;gap:${spacing.sm};flex:1;color:${colors.text.secondary};`;
const SearchInput = styled.input`
  border:none;outline:none;background:none;flex:1;
  font-size:${typography.fontSize.sm};color:${colors.text.primary};
  &::placeholder{color:${colors.text.tertiary};}
`;
const Dropdown = styled.div`
  position:absolute;top:100%;left:0;right:0;z-index:100;
  max-height:240px;overflow-y:auto;
  background:${colors.background.default};
  border:1px solid ${colors.border.light};border-top:none;
  border-radius:0 0 ${borderRadius.sm} ${borderRadius.sm};
  box-shadow:${colors.shadows?.md || '0 3px 6px rgba(0,0,0,0.15)'};
`;
const DropdownItem = styled.div`
  display:flex;align-items:center;gap:${spacing.sm};
  padding:8px ${spacing.md};cursor:pointer;
  background:${props => props.$highlighted ? colors.primary.main + '15' : props.$selected ? colors.primary.main + '10' : 'transparent'};
  border-left:${props => props.$selected ? `3px solid ${colors.primary.main}` : '3px solid transparent'};
  &:hover{background:${colors.neutral[50]};}
  &:not(:last-child){border-bottom:1px solid ${colors.neutral[100]};}
`;
const VendorCode = styled.span`
  font-size:${typography.fontSize.xs};font-weight:${typography.fontWeight.semibold};
  color:${colors.primary.main};background:${colors.primary.main}10;
  padding:2px 6px;border-radius:3px;white-space:nowrap;
`;
const PartyBadge = styled.span`
  display:inline-block;width:18px;height:18px;
  background:${props => props.$customer ? colors.info.main + '25' : colors.warning.main + '25'};
  color:${props => props.$customer ? colors.info.dark : colors.warning.dark};
  border-radius:4px;font-size:10px;font-weight:700;
  text-align:center;line-height:18px;
  flex-shrink:0;
`;
const VendorName = styled.span`font-size:${typography.fontSize.sm};color:${colors.text.primary};flex:1;`;
const VendorCity = styled.span`font-size:${typography.fontSize.xs};color:${colors.text.tertiary};`;
const NoResults = styled.div`padding:${spacing.md};text-align:center;color:${colors.text.tertiary};font-size:${typography.fontSize.sm};`;

const VendorInfoPanel = styled.div`
  margin-top:6px;padding:8px ${spacing.md};
  background:${colors.neutral[50]};border:1px solid ${colors.border.light};
  border-radius:${borderRadius.sm};
`;
const InfoRow = styled.div`display:flex;flex-wrap:wrap;gap:${spacing.sm};align-items:center;`;
const LedgerLinkChip = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.primary.main};
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  padding: 2px 10px;
  border-radius: 12px;
  cursor: pointer;
  &:hover { background: ${colors.primary.main}; color: white; }
`;

const InfoChip = styled.span`
  display:inline-flex;align-items:center;gap:4px;
  font-size:${typography.fontSize.xs};
  color:${props => props.$negative ? colors.error.main : props.$highlight ? colors.primary.main : colors.text.secondary};
  font-weight:${props => props.$highlight ? typography.fontWeight.semibold : typography.fontWeight.regular};
  padding:2px 8px;background:${colors.background.default};border:1px solid ${colors.border.light};
  border-radius:12px;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export default VendorSelect;
